import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Wrench, 
  Image, 
  Settings, 
  MessageSquare, 
  LogOut, 
  Camera,
  Menu,
  X,
  FolderOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import DashboardOverview from '@/components/admin/DashboardOverview';
import ServicesManager from '@/components/admin/ServicesManager';
import GalleryManager from '@/components/admin/GalleryManager';
import SettingsManager from '@/components/admin/SettingsManager';
import QuotesManager from '@/components/admin/QuotesManager';
import RecentWorksManager from '@/components/admin/RecentWorksManager';

const navItems = [
  { path: '/admin/dashboard', label: 'Przegląd', icon: LayoutDashboard, exact: true },
  { path: '/admin/dashboard/services', label: 'Usługi', icon: Wrench },
  { path: '/admin/dashboard/gallery', label: 'Galeria', icon: Image },
  { path: '/admin/dashboard/recent-works', label: 'Ostatnie Realizacje', icon: FolderOpen },
  { path: '/admin/dashboard/quotes', label: 'Zapytania', icon: MessageSquare },
  { path: '/admin/dashboard/settings', label: 'Ustawienia', icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, username, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C]" data-testid="admin-dashboard">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#111827] border-b border-cyan-500/10 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center">
            <Camera className="w-4 h-4 text-[#0A0F1C]" />
          </div>
          <span className="font-bold text-white">CAM VISION</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-slate-400 hover:text-cyan-400"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-[#111827] transform transition-transform duration-300 z-50 lg:translate-x-0 border-r border-cyan-500/10 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-20 flex items-center px-6 border-b border-cyan-500/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Camera className="w-5 h-5 text-[#0A0F1C]" />
              </div>
              <div>
                <span className="font-bold text-white block">CAM VISION</span>
                <span className="text-xs text-slate-400">Panel Admin</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-3 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.exact}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 text-cyan-400 border border-cyan-500/20'
                          : 'text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/5'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-cyan-500/10">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 border border-cyan-500/20 rounded-full flex items-center justify-center">
                <span className="text-cyan-400 font-medium">
                  {username?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{username}</p>
                <p className="text-xs text-slate-400">Administrator</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/5"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Wyloguj się
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="services" element={<ServicesManager />} />
            <Route path="gallery" element={<GalleryManager />} />
            <Route path="recent-works" element={<RecentWorksManager />} />
            <Route path="quotes" element={<QuotesManager />} />
            <Route path="settings" element={<SettingsManager />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
